/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { colors } from '@smartsheet/lodestar-core';
import { Icon } from '@smartsheet/lodestar-icons';
import React, { DragEventHandler, useState } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useDispatch, useSelector } from 'react-redux';
import {
  GraphNodeTypes,
  selectGrabbedItem,
  setSelectedElementID,
} from '../../../../services/ducks/designerDuck/designerDuck';
import { selectModuleById } from '../../../../services/ducks/modulesDuck/modulesDuck';
import {
  graphSelector,
  insertNodeIntoGraph,
  moveNode,
  WorkflowStateNode,
} from '../../../../services/ducks/workflowDuck/workflowDuck';
import { NODE_HEIGHT } from '../../../../utils/dimensions';
import { DragHandleIcon } from '../../../icons/DragHandleIcon';
import { StateIcon } from '../../../icons/StateIcon';
import { dropLocationsRules } from '../Canvas';
import { IconBox, Label, Node } from './styled';
import { generateFourDigitNumber, isGrabbedItemExisting } from './utils';

type GroupExitNodeData = {
  data: WorkflowStateNode;
  label: JSX.Element;
} & {
  data: {
    type: GraphNodeTypes.STATE;
  };
};

export const GroupExit = ({
  data,
  forReactFlow = true,
}: NodeProps<GroupExitNodeData> & { forReactFlow: boolean }) => {
  const [hover, setHover] = useState<boolean>(false);
  const dispatch = useDispatch();
  const grabbedItem = useSelector(selectGrabbedItem);
  const graph = useSelector(graphSelector);
  const module = useSelector(
    selectModuleById(grabbedItem?.moduleID ?? '', grabbedItem?.pluginUUID ?? '')
  );

  const dropHandler: DragEventHandler<HTMLDivElement> = event => {
    if (grabbedItem) {
      const canAddNode = dropLocationsRules[grabbedItem.type];
      if (canAddNode(data.data.id, graph.items, grabbedItem.id)) {
        event?.preventDefault();
        if (!grabbedItem) return;
        const insertPayload = {
          parentID: data.data.id,
          name: grabbedItem.state ?? `State ${generateFourDigitNumber()}`,
          nodeToInsert: grabbedItem,
          module,
        };

        if (isGrabbedItemExisting(grabbedItem)) {
          dispatch(moveNode(insertPayload));
        } else {
          dispatch(insertNodeIntoGraph(insertPayload));
        }
        dispatch(setSelectedElementID(grabbedItem.id));
      } else {
        return;
      }
    }
  };

  return (
    <Node
      selected={false}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDrop={dropHandler}
      onDragEnter={undefined}
      onDragOver={event => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      {forReactFlow ? (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={true}
          style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
        />
      ) : null}
      <IconBox color={colors.visBerryLight10} hover={hover}>
        <Icon
          svgComponent={hover ? DragHandleIcon : StateIcon}
          size={'xxSmall'}
        />
      </IconBox>
      <Label
        selected={false}
        css={css`
          color: ${colors.neutralDark10};
        `}
      >
        {data.label}
      </Label>
      {forReactFlow ? (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={true}
          style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
        />
      ) : null}
    </Node>
  );
};
